import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Upload, Download, CheckCircle2, AlertCircle, Loader2, ShieldCheck, Trash2, Calculator } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ProofAiSummary } from "@/components/admin/LeadProofsReview";
import { LeadStageProgress } from "@/components/admin/LeadStageControl";
import LeadDetailPanel from "@/components/LeadDetailPanel";
import { GlobalSearch } from "@/components/GlobalSearch";
import { RecordCard, CardGrid, FilterStrip, StatusBadge, Chip, EmptyTile, DetailBoard, Tile, btn } from "@/components/bento";

const STATUS_COLOR = {
  Submitted: "bg-stone-200 text-stone-700",
  Verification: "bg-amber-100 text-amber-800",
  Approved: "bg-blue-100 text-blue-800",
  Rejected: "bg-red-100 text-red-700",
  Paid: "bg-emerald-100 text-emerald-800",
};

const PROOF_PILL = {
  pending_review: { label: "Proof under review", tone: "bg-amber-100 text-amber-800" },
  verified: { label: "Proof verified", tone: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Proof rejected", tone: "bg-red-100 text-red-700" },
};

const PROOF_STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800",
  verified: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

export default function MyLeads() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const hideMoney = !!authUser?.corporate_id;
  const [leads, setLeads] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const fileRefs = useRef({});

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const isMotorPolicyEligible = (l) =>
    (l.product_category === "Motor Insurance" || (l.product_name || "").toLowerCase().includes("motor") || (l.product_name || "").toLowerCase().includes("car") || (l.product_name || "").toLowerCase().includes("two-wheeler")) &&
    !!l.verified;

  const onPickFile = (leadId) => fileRefs.current[leadId]?.click();

  const onFileSelected = async (leadId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a .pdf file");
      e.target.value = ""; return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large — max 15 MB");
      e.target.value = ""; return;
    }
    setUploadingId(leadId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/leads/${leadId}/upload-policy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = r.data;
      setLeads((prev) => prev.map((x) => x.id === leadId ? updated : x));
      if (updated.policy_extraction_status === "success") {
        toast.success("Policy uploaded — details extracted automatically");
      } else if (updated.policy_extraction_status === "failed") {
        toast.warning("Policy uploaded but auto-extraction failed. You can re-upload a clearer scan.");
      } else {
        toast.success("Policy uploaded");
      }
      setExpanded(leadId);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  const downloadPdf = async (lead) => {
    try {
      const res = await api.get(`/leads/${lead.id}/policy-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = lead.policy_pdf_filename || `crece-policy-${lead.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) { toast.error("Download failed"); }
  };

  const STATUSES = ["Submitted", "Verification", "Approved", "Paid", "Rejected"];
  const filtered = leads.filter((l) => (filter === "all" || l.status === filter) && (!q || `${l.customer_name} ${l.customer_mobile} ${l.product_name} ${l.ref_number || ""}`.toLowerCase().includes(q.toLowerCase())));
  const chips = [{ k: "all", label: "All", count: leads.length }, ...STATUSES.map((s) => ({ k: s, label: s, count: leads.filter((l) => l.status === s).length })).filter((c) => c.count > 0)];

  return (
    <div className="space-y-6" data-testid="my-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Pipeline</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">My Leads</h1>
        </div>
        <Link to="/app/products" className={btn.accent} data-testid="my-leads-new">+ Submit New Lead</Link>
      </div>

      <GlobalSearch
        endpoint="/search/mine"
        placeholder="Search your leads, quotes & claims by ref no, name or mobile..."
        onSelect={(r) => { if (r.type === "lead") setExpanded(r.id); }}
      />

      {leads.length > 0 && (
        <FilterStrip testId="leads-filter" presetKey="my_leads" chips={chips} active={filter} onChange={setFilter} search={q} onSearch={setQ} searchPlaceholder="Filter by name, mobile, product or ref…" />
      )}

      {leads.length === 0 ? (
        <EmptyTile testId="leads-empty" title="No leads yet" desc="Recommend a product to a customer and your pipeline will show up here as cards."
          action={<Link to="/app/products" className={btn.primary}>Browse products →</Link>} />
      ) : (
        <CardGrid cols={3} testId="leads-table">
          {filtered.length === 0 && <EmptyTile testId="leads-filter-empty" title="No leads match" desc="Try a different status or clear the search." />}
          {filtered.map((l) => {
            const motorEligible = isMotorPolicyEligible(l);
            const hasPdf = !!l.policy_pdf_uploaded_at;
            const isExp = expanded === l.id;
            return (
              <RecordCard key={l.id} testId={`lead-row-${l.id}`} expanded={isExp} onClick={() => setExpanded(isExp ? null : l.id)}
                title={l.customer_name} code={l.ref_number && <span data-testid={`lead-ref-${l.id}`}>{l.ref_number}</span>}
                sub={l.product_name}
                badge={<span className="flex items-center gap-1"><StatusBadge status={l.status} className={STATUS_COLOR[l.status]} testId={`lead-status-${l.id}`} />{l.priority?.score != null && <Chip tone={l.priority.score >= 70 ? "red" : l.priority.score >= 45 ? "amber" : "neutral"} testId={`lead-priority-${l.id}`}>{l.priority.score >= 70 ? "🔥 " : ""}{l.priority.label} · {l.priority.score}</Chip>}</span>}
                right={!hideMoney && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Commission</div>
                    <div className="font-mono font-bold text-stone-900">{l.commission_amount ? formatINR(l.commission_amount) : "—"}</div>
                    {(l.basis_amount || l.disbursed_amount) && (
                      <div className="text-[10px] font-bold text-violet-700 mt-0.5" data-testid={`my-lead-basis-${l.id}`}>
                        {l.basis_amount ? "Premium/Basis" : "Disbursed"} ₹{Number(l.basis_amount || l.disbursed_amount).toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                )}
                meta={[
                  l.customer_mobile && <span className="font-mono">+91 {l.customer_mobile}</span>,
                  <span>{new Date(l.created_at).toLocaleDateString()}</span>,
                  l.proof_status && <Chip testId={`lead-proof-pill-${l.id}`} className={PROOF_PILL[l.proof_status]?.tone}>{PROOF_PILL[l.proof_status]?.label || l.proof_status}</Chip>,
                  motorEligible && hasPdf && <Chip tone="emerald" testId={`lead-policy-pill-${l.id}`}><ShieldCheck className="w-3 h-3" /> Policy on file</Chip>,
                ]}>
                {isExp && (
                  <DetailBoard testId={`lead-policy-panel-${l.id}`}>
                    <Tile span={6} title="Stage" icon={CheckCircle2}><LeadStageProgress lead={l} /></Tile>
                    <Tile span={6} title="Lead board" icon={FileText}><LeadDetailPanel lead={l} /></Tile>
                    <Tile span={6} tone="bg-amber-50/60 border-amber-200"><ProofSection lead={l} onChanged={load} /></Tile>
                    {/health/i.test(`${l.product_category || ""} ${l.product_name || ""}`) && !["Paid", "Rejected"].includes(l.status) && (
                      <Tile span={6} tone="bg-violet-50/60 border-violet-200">
                        <div className="flex items-start gap-3 flex-wrap">
                          <div className="w-10 h-10 rounded-xl bg-violet-800 text-white flex items-center justify-center shrink-0"><Calculator className="w-5 h-5" /></div>
                          <div className="flex-1 min-w-[200px]">
                            <div className="font-black text-sm">Price &amp; compare health plans for {l.customer_name}</div>
                            <div className="text-xs text-stone-500 mt-0.5">Rate-card premiums from the insurers' own charts, side-by-side with other market plans. The plan you pick is attached to this lead.</div>
                          </div>
                          <Button size="sm" onClick={() => navigate("/app/premium-calculator", { state: { prefill: { lead_id: l.id, lead_ref: l.ref_number, client_name: l.customer_name, client_mobile: l.customer_mobile, client_id: l.client_id || null, city: l.city || "", sum_insured: l.quote_snapshot?.sum_insured || 1000000, members: l.quote_snapshot?.members?.length ? l.quote_snapshot.members : undefined } } })}
                            className="h-9 rounded-full bg-violet-800 hover:bg-violet-900 text-white text-xs font-bold" data-testid={`lead-price-plans-${l.id}`}><Calculator className="w-3.5 h-3.5 mr-1" /> Price &amp; compare plans</Button>
                        </div>
                      </Tile>
                    )}
                    {motorEligible && (
                      <Tile span={6}>
                          <div className="flex items-start gap-3 flex-wrap">
                            <div className="w-10 h-10 rounded-xl bg-emerald-900 text-saffron-400 flex items-center justify-center shrink-0">
                              <FileText className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <div className="font-display text-lg font-black">Motor Insurance Policy Document</div>
                              {!hasPdf ? (
                                <p className="text-sm text-stone-600 mt-1">
                                  Once the bank has issued the policy, upload the PDF here. We&apos;ll auto-extract the policy number,
                                  insured name and validity dates so your customer always has them handy.
                                </p>
                              ) : (
                                <div className="mt-2 space-y-2">
                                  {l.policy_extraction_status === "pending" && (
                                    <div className="text-xs text-stone-500 inline-flex items-center" data-testid={`lead-policy-status-pending-${l.id}`}>
                                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting fields...
                                    </div>
                                  )}
                                  {l.policy_extraction_status === "failed" && (
                                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 inline-flex items-center gap-2" data-testid={`lead-policy-status-failed-${l.id}`}>
                                      <AlertCircle className="w-3.5 h-3.5" /> Auto-extraction failed. Please re-upload a clearer PDF.
                                    </div>
                                  )}
                                  {l.policy_extraction_status === "success" && (
                                    <div className="space-y-3" data-testid={`lead-policy-fields-${l.id}`}>
                                      <div className="grid sm:grid-cols-4 gap-3">
                                        <Field label="Policy number" value={l.policy_number} testid={`lead-policy-number-${l.id}`} />
                                        <Field label="Insured name" value={l.policy_insured_name} testid={`lead-policy-insured-${l.id}`} />
                                        <Field label="Start date" value={l.policy_start_date} testid={`lead-policy-start-${l.id}`} mono />
                                        <Field label="End date" value={l.policy_end_date} testid={`lead-policy-end-${l.id}`} mono />
                                      </div>
                                      <div className="grid sm:grid-cols-5 gap-3">
                                        <Field label="Vehicle make" value={l.policy_vehicle_make} testid={`lead-policy-make-${l.id}`} />
                                        <Field label="Vehicle model" value={l.policy_vehicle_model} testid={`lead-policy-model-${l.id}`} />
                                        <Field label="Net premium" value={l.policy_premium_net ? formatINR(l.policy_premium_net) : ""} testid={`lead-policy-premium-net-${l.id}`} mono />
                                        <Field label="GST" value={l.policy_premium_gst ? formatINR(l.policy_premium_gst) : ""} testid={`lead-policy-premium-gst-${l.id}`} mono />
                                        <Field label="Total premium" value={l.policy_premium_total ? formatINR(l.policy_premium_total) : ""} testid={`lead-policy-premium-total-${l.id}`} mono />
                                      </div>
                                    </div>
                                  )}
                                  <div className="text-[10px] text-stone-400">
                                    Uploaded {l.policy_pdf_uploaded_at ? new Date(l.policy_pdf_uploaded_at).toLocaleString() : "—"} · {l.policy_pdf_filename || "policy.pdf"}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 items-center">
                              {hasPdf && (
                                <Button variant="outline" onClick={(e) => { e.stopPropagation(); downloadPdf(l); }} data-testid={`lead-policy-download-${l.id}`} className="rounded-full text-xs h-9">
                                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                                </Button>
                              )}
                              <Button
                                onClick={(e) => { e.stopPropagation(); onPickFile(l.id); }}
                                disabled={uploadingId === l.id}
                                data-testid={`lead-policy-upload-${l.id}`}
                                className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold text-xs h-9">
                                {uploadingId === l.id ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading...</>) : hasPdf ? (<><Upload className="w-3.5 h-3.5 mr-1.5" /> Replace PDF</>) : (<><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Policy PDF</>)}
                              </Button>
                              <input
                                ref={(el) => { fileRefs.current[l.id] = el; }}
                                type="file" accept=".pdf,application/pdf" hidden
                                onChange={(e) => onFileSelected(l.id, e)}
                                data-testid={`lead-policy-upload-input-${l.id}`}
                              />
                            </div>
                          </div>
                      </Tile>
                    )}
                  </DetailBoard>
                )}
              </RecordCard>
            );
          })}
        </CardGrid>
      )}
    </div>
  );
}

function ProofSection({ lead, onChanged }) {
  const [data, setData] = useState(null);
  const [type, setType] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/leads/${lead.id}/proofs`).then((r) => {
    setData(r.data);
    setType((t) => r.data.allowed_types?.some((a) => a.key === t) ? t : (r.data.allowed_types?.[0]?.key || ""));
  }).catch(() => {});
  useEffect(() => { load(); }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if ((data?.items || []).some((p) => p.ai?.status === "pending")) {
      const t = setTimeout(load, 4000);
      return () => clearTimeout(t);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) { toast.error("PDF, JPG, PNG or WebP only"); e.target.value = ""; return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("File too large — max 3 MB"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const dataUri = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      await api.post(`/leads/${lead.id}/proofs`, { type, filename: file.name, data_base64: dataUri });
      toast.success("Proof uploaded — Ops will verify it shortly");
      load(); onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const view = async (p) => {
    try {
      const r = await api.get(`/leads/${lead.id}/proofs/${p.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: p.content_type }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open proof"); }
  };

  const removeProof = async (p) => {
    if (!window.confirm(`Remove this ${p.label.toLowerCase()} (${p.filename})? You can upload the correct document after.`)) return;
    try {
      await api.delete(`/leads/${lead.id}/proofs/${p.id}`);
      toast.success("Proof removed — upload the correct one now");
      load(); onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove proof"); }
  };

  const items = data?.items || [];
  return (
    <div data-testid={`lead-proof-section-${lead.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display text-lg font-black">Completion Proof <span className="text-red-600 text-sm align-super">*</span></div>
          <p className="text-sm text-stone-600 mt-0.5">
            Once your client completes the purchase, upload {(data?.allowed_types || []).map((t) => t.label.toLowerCase()).join(" or ") || "proof"}.
            Operations must verify it before this lead can be approved.
          </p>
          {items.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {items.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-2.5 bg-white border border-stone-200 rounded-lg px-3 py-2 text-xs" data-testid={`lead-proof-row-${p.id}`}>
                  <FileText className="w-3.5 h-3.5 text-stone-400" />
                  <span className="font-bold">{p.label}</span>
                  <span className="text-stone-500 truncate max-w-[160px]">{p.filename}</span>
                  <span className={`px-2 py-0.5 rounded-full font-bold ${PROOF_STATUS_TONE[p.status]}`} data-testid={`lead-proof-status-${p.id}`}>{p.status}</span>
                  {p.status === "rejected" && p.remark && <span className="text-red-700">— {p.remark}</span>}
                  <button onClick={(e) => { e.stopPropagation(); view(p); }} className="ml-auto font-bold text-emerald-800 underline underline-offset-2" data-testid={`lead-proof-view-${p.id}`}>View</button>
                  {p.status !== "verified" && (
                    <button onClick={(e) => { e.stopPropagation(); removeProof(p); }} title="Remove & re-upload"
                      className="inline-flex items-center gap-0.5 font-bold text-red-600 underline underline-offset-2" data-testid={`lead-proof-delete-${p.id}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  )}
                  <ProofAiSummary ai={p.ai} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {(data?.allowed_types || []).length > 1 && (
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid={`lead-proof-type-${lead.id}`}
              className="h-9 rounded-full border border-stone-300 bg-white px-3 text-xs font-bold">
              {(data?.allowed_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          )}
          <Button onClick={() => fileRef.current?.click()} disabled={uploading || !type}
            data-testid={`lead-proof-upload-${lead.id}`}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs h-9">
            {uploading ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>) : (<><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload proof</>)}
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" hidden onChange={onFile}
            data-testid={`lead-proof-input-${lead.id}`} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
      <div data-testid={testid} className={`mt-1 text-sm text-stone-900 ${mono ? "font-mono" : "font-semibold"}`}>
        {value || <span className="text-stone-400">—</span>}
      </div>
      {value && <CheckCircle2 className="w-3 h-3 text-emerald-600 inline-block ml-1" />}
    </div>
  );
}
